import React, { useState, useEffect, useCallback } from 'react';
import {
  Buildings, Plus, Trash, PencilSimple, Calendar, Calculator, FilePdf,
  Money, CaretRight, X, CheckCircle, Warning, Car,
} from '@phosphor-icons/react';
import { supplierApi, settlementApi, vehicleApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

const eur = (n) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));
const todayISO = () => new Date().toISOString().substring(0, 10);
const firstDayOfMonth = () => {
  const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const lastDayOfMonth = () => {
  const d = new Date(); const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return last.toISOString().substring(0, 10);
};
const quarterRange = (year, q) => {
  const startMonth = (q - 1) * 3;
  const start = new Date(year, startMonth, 1);
  const end = new Date(year, startMonth + 3, 0);
  return [start.toISOString().substring(0, 10), end.toISOString().substring(0, 10)];
};

const emptySupplier = {
  name: '', contact_person: '', email: '', phone: '',
  address_street: '', address_zip: '', address_city: '', address_country: 'DE',
  vat_id: '', tax_number: '', bank_name: '', iban: '', bic: '',
  default_share_pct: 70, payment_terms_days: 14, notes: '', active: true,
};

const SupplierSettlements = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(firstDayOfMonth());
  const [periodTo, setPeriodTo] = useState(lastDayOfMonth());
  const [basis, setBasis] = useState('ist');
  const [vatRate, setVatRate] = useState(19);
  const [postToCashbook, setPostToCashbook] = useState(true);
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState({}); // {id: true|false}
  const [shareOverrides, setShareOverrides] = useState({});         // {id: pct}
  const [creating, setCreating] = useState(false);

  // Supplier CRUD modal
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierForm, setSupplierForm] = useState(emptySupplier);
  const [supplierEditingId, setSupplierEditingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [sup, veh, st] = await Promise.all([
        supplierApi.getAll(),
        vehicleApi.getAll(),
        settlementApi.list(),
      ]);
      setSuppliers(sup.data || []);
      setVehicles(veh.data || []);
      setSettlements(st.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // When supplier changes → auto-select all its vehicles
  useEffect(() => {
    if (!selectedSupplierId) { setSelectedVehicleIds({}); return; }
    const own = vehicles.filter((v) => v.supplier_id === selectedSupplierId).map((v) => v.id);
    const sel = {};
    own.forEach((id) => { sel[id] = true; });
    setSelectedVehicleIds(sel);
    setShareOverrides({});
    setPreview(null);
  }, [selectedSupplierId, vehicles]);

  const supplierVehicles = vehicles.filter((v) => v.supplier_id === selectedSupplierId);
  const selectedSupplier = suppliers.find((s) => s.id === selectedSupplierId);

  const applyQuickRange = (kind, arg) => {
    const y = new Date().getFullYear();
    if (kind === 'thisMonth') { setPeriodFrom(firstDayOfMonth()); setPeriodTo(lastDayOfMonth()); return; }
    if (kind === 'lastMonth') {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
      const first = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().substring(0, 10);
      setPeriodFrom(first); setPeriodTo(last); return;
    }
    if (kind === 'quarter') { const [a, b] = quarterRange(y, arg); setPeriodFrom(a); setPeriodTo(b); return; }
    if (kind === 'year') { setPeriodFrom(`${y}-01-01`); setPeriodTo(`${y}-12-31`); return; }
  };

  const runPreview = async () => {
    if (!selectedSupplierId) { toast.error('Bitte Lieferanten auswählen'); return; }
    const vehicleIdsCsv = supplierVehicles
      .filter((v) => selectedVehicleIds[v.id])
      .map((v) => v.id).join(',');
    if (!vehicleIdsCsv) { toast.error('Bitte mindestens 1 Fahrzeug auswählen'); return; }
    setPreviewLoading(true);
    try {
      const res = await settlementApi.preview({
        supplier_id: selectedSupplierId,
        period_from: periodFrom, period_to: periodTo,
        basis, vehicle_ids: vehicleIdsCsv,
      });
      setPreview(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPreviewLoading(false); }
  };

  const overridePct = (vid, val) => {
    const num = parseFloat(val);
    setShareOverrides((prev) => ({ ...prev, [vid]: Number.isNaN(num) ? undefined : num }));
  };

  // Anteil-% wird auf den Brutto-Umsatz angewendet → payout ist BRUTTO.
  // MwSt wird aus dem Brutto HERAUSGERECHNET (nicht draufaddiert), da der
  // Kunde bereits Brutto (inkl. MwSt) bezahlt hat.
  const displayedLineItems = preview ? preview.line_items.map((li) => {
    const overridden = shareOverrides[li.vehicle_id];
    const pct = overridden !== undefined ? overridden : li.share_pct;
    const payout = Math.round(li.revenue * pct) / 100;
    return { ...li, effective_pct: pct, effective_payout: payout };
  }) : [];
  const totalRevenue = displayedLineItems.reduce((s, x) => s + x.revenue, 0);
  const totalGross = displayedLineItems.reduce((s, x) => s + x.effective_payout, 0);
  const divisor = vatRate ? (1 + vatRate / 100) : 1;
  const totalNet = totalGross / divisor;
  const totalVat = totalGross - totalNet;

  const createSettlement = async () => {
    if (!preview) { toast.error('Bitte zuerst Vorschau berechnen'); return; }
    setCreating(true);
    try {
      const overrides = Object.entries(shareOverrides)
        .filter(([, pct]) => pct !== undefined)
        .map(([vehicle_id, share_pct]) => ({ vehicle_id, share_pct }));
      const vehicleIds = displayedLineItems.map((l) => l.vehicle_id);
      const res = await settlementApi.create({
        supplier_id: selectedSupplierId,
        period_from: periodFrom, period_to: periodTo,
        basis,
        vehicle_ids: vehicleIds,
        line_overrides: overrides,
        notes: notes.trim() || null,
        post_to_cashbook: postToCashbook,
        vat_rate: parseFloat(vatRate),
      });
      toast.success(`Abrechnung ${res.data.settlement_number} erstellt`);
      // Trigger PDF download
      window.open(settlementApi.pdfUrl(res.data.id), '_blank');
      await load();
      setPreview(null);
      setNotes('');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCreating(false); }
  };

  const openSupplierEdit = (s) => {
    setSupplierEditingId(s?.id || null);
    setSupplierForm(s ? {
      name: s.name || '', contact_person: s.contact_person || '', email: s.email || '', phone: s.phone || '',
      address_street: s.address_street || '', address_zip: s.address_zip || '', address_city: s.address_city || '', address_country: s.address_country || 'DE',
      vat_id: s.vat_id || '', tax_number: s.tax_number || '',
      bank_name: s.bank_name || '', iban: s.iban || '', bic: s.bic || '',
      default_share_pct: s.default_share_pct ?? 70, payment_terms_days: s.payment_terms_days ?? 14,
      notes: s.notes || '', active: s.active !== false,
    } : emptySupplier);
    setShowSupplierModal(true);
  };

  const saveSupplier = async () => {
    if (!supplierForm.name.trim()) { toast.error('Name ist Pflicht'); return; }
    try {
      if (supplierEditingId) {
        await supplierApi.update(supplierEditingId, supplierForm);
        toast.success('Lieferant aktualisiert');
      } else {
        await supplierApi.create(supplierForm);
        toast.success('Lieferant angelegt');
      }
      setShowSupplierModal(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSupplier = async (s) => {
    if (!window.confirm(`Lieferant „${s.name}" wirklich löschen?`)) return;
    try {
      await supplierApi.remove(s.id);
      toast.success('Gelöscht');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteSettlement = async (st) => {
    if (!window.confirm(`Abrechnung ${st.settlement_number} löschen? Der zugehörige Kassenbuch-Eintrag wird ebenfalls entfernt.`)) return;
    try {
      await settlementApi.remove(st.id);
      toast.success('Abrechnung gelöscht');
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1400px]" data-testid="supplier-settlements-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Buildings weight="fill" size={26} className="text-[#E60000]" />
            Lieferanten-Abrechnung
          </h1>
          <p className="text-sm text-[#525866]">Umsatzbeteiligung berechnen & Gutschriftverfahren-PDF erstellen</p>
        </div>
        <Button variant="outline" onClick={() => openSupplierEdit(null)} data-testid="new-supplier-btn">
          <Plus size={14} className="mr-1" /> Neuer Lieferant
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: Supplier list */}
        <div className="card-swiss p-4 space-y-2 lg:col-span-1">
          <h2 className="font-bold text-[#111418] mb-2">Lieferanten ({suppliers.length})</h2>
          {suppliers.length === 0 && (
            <p className="text-sm text-[#8A94A6]">Noch keine Lieferanten. Lege den ersten mit dem Button oben rechts an.</p>
          )}
          {suppliers.map((s) => {
            const active = selectedSupplierId === s.id;
            return (
              <div
                key={s.id}
                className={`p-3 rounded-sm border cursor-pointer transition ${active ? 'bg-[#FFF5F5] border-[#E60000]' : 'bg-white border-[#E2E4E9] hover:border-[#8A94A6]'}`}
                onClick={() => setSelectedSupplierId(s.id)}
                data-testid={`supplier-item-${s.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-[#111418] truncate">{s.name}</p>
                    <p className="text-xs text-[#8A94A6] flex items-center gap-2 mt-0.5">
                      <span><Car size={11} className="inline mr-0.5" />{s.vehicle_count} Fzg.</span>
                      <span>· {s.default_share_pct}% Anteil</span>
                      {!s.active && <span className="text-[#E60000]">· inaktiv</span>}
                    </p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); openSupplierEdit(s); }} data-testid={`edit-supplier-${s.id}`}>
                      <PencilSimple size={12} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-[#E60000]" onClick={(e) => { e.stopPropagation(); deleteSupplier(s); }} data-testid={`delete-supplier-${s.id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Recent Settlements */}
          <div className="pt-4 border-t border-[#E2E4E9] mt-4">
            <h2 className="font-bold text-[#111418] mb-2">Letzte Abrechnungen ({settlements.length})</h2>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {settlements.length === 0 && <p className="text-xs text-[#8A94A6]">Noch keine Abrechnungen erstellt.</p>}
              {settlements.map((st) => (
                <div key={st.id} className="p-2 border border-[#E2E4E9] rounded-sm text-xs" data-testid={`settlement-item-${st.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="min-w-0 flex-1">
                      <p className="font-mono font-semibold text-[#111418]">{st.settlement_number}</p>
                      <p className="text-[#8A94A6] truncate">{st.supplier_snapshot?.name}</p>
                      <p className="text-[#525866] mt-0.5">{st.period_from} – {st.period_to}</p>
                      <p className="text-[#009951] font-bold mt-0.5">{eur(st.totals?.payout_gross)}</p>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <a href={settlementApi.pdfUrl(st.id)} target="_blank" rel="noreferrer" className="text-[#E60000]" title="PDF">
                        <FilePdf size={14} />
                      </a>
                      <button onClick={() => deleteSettlement(st)} className="text-[#8A94A6] hover:text-[#E60000]" title="Löschen">
                        <Trash size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: New settlement */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedSupplier ? (
            <div className="card-swiss p-8 text-center text-[#8A94A6]">
              <Buildings size={48} className="mx-auto mb-3 opacity-40" />
              <p className="font-semibold">Wähle links einen Lieferanten aus</p>
              <p className="text-sm mt-1">Danach kannst du Zeitraum, Fahrzeuge und Berechnungsbasis wählen und die Gutschrift-Abrechnung erstellen.</p>
            </div>
          ) : (
            <>
              <div className="card-swiss p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="font-bold text-[#111418] flex items-center gap-2">
                      <Buildings size={16} className="text-[#E60000]" />
                      {selectedSupplier.name}
                    </h2>
                    <p className="text-xs text-[#8A94A6]">
                      Standard-Anteil: <strong>{selectedSupplier.default_share_pct}%</strong>
                      {selectedSupplier.vat_id && ` · USt-IdNr. ${selectedSupplier.vat_id}`}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-1 bg-[#F7F8FA] rounded-sm">{supplierVehicles.length} Fahrzeug{supplierVehicles.length !== 1 ? 'e' : ''}</span>
                </div>
              </div>

              {/* Period */}
              <div className="card-swiss p-4">
                <h3 className="font-bold text-[#111418] mb-3 flex items-center gap-2"><Calendar size={16} /> Zeitraum</h3>
                <div className="flex flex-wrap gap-2 mb-3">
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('thisMonth')} data-testid="period-this-month">Aktueller Monat</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('lastMonth')} data-testid="period-last-month">Letzter Monat</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('quarter', 1)}>Q1</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('quarter', 2)}>Q2</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('quarter', 3)}>Q3</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('quarter', 4)}>Q4</Button>
                  <Button variant="outline" size="sm" onClick={() => applyQuickRange('year')} data-testid="period-year">Ganzes Jahr</Button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="overline mb-1 block">Von</Label>
                    <Input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} data-testid="period-from" />
                  </div>
                  <div>
                    <Label className="overline mb-1 block">Bis</Label>
                    <Input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} data-testid="period-to" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="overline mb-1 block">Umsatzbasis</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={basis === 'ist' ? 'default' : 'outline'} size="sm"
                      className={basis === 'ist' ? 'btn-primary' : ''}
                      onClick={() => setBasis('ist')} data-testid="basis-ist"
                    >
                      Ist (bezahlt)
                    </Button>
                    <Button
                      variant={basis === 'soll' ? 'default' : 'outline'} size="sm"
                      className={basis === 'soll' ? 'btn-primary' : ''}
                      onClick={() => setBasis('soll')} data-testid="basis-soll"
                    >
                      Soll (Buchungsanteil)
                    </Button>
                  </div>
                  <p className="text-xs text-[#8A94A6] mt-1">
                    <strong>Ist</strong>: nur bezahlte Rechnungen im Zeitraum &nbsp;•&nbsp; <strong>Soll</strong>: anteiliger Mietpreis (Tage-basiert)
                  </p>
                </div>
              </div>

              {/* Vehicles selection */}
              <div className="card-swiss p-4">
                <h3 className="font-bold text-[#111418] mb-3 flex items-center gap-2"><Car size={16} /> Fahrzeuge einbeziehen</h3>
                {supplierVehicles.length === 0 ? (
                  <p className="text-sm text-[#E60000] flex items-center gap-1.5"><Warning size={14} /> Diesem Lieferanten sind noch keine Fahrzeuge zugeordnet. Öffne ein Fahrzeug und setze &bdquo;Eigentumsart = Lieferant&ldquo; + Lieferant-Auswahl.</p>
                ) : (
                  <div className="space-y-1">
                    {supplierVehicles.map((v) => (
                      <label
                        key={v.id}
                        className="flex items-center gap-3 p-2 border border-[#E2E4E9] rounded-sm hover:bg-[#F7F8FA] cursor-pointer"
                        data-testid={`vehicle-toggle-${v.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={!!selectedVehicleIds[v.id]}
                          onChange={(e) => setSelectedVehicleIds({ ...selectedVehicleIds, [v.id]: e.target.checked })}
                          className="w-4 h-4 accent-[#E60000]"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#111418]">{v.brand} {v.model}</p>
                          <p className="text-xs text-[#8A94A6] font-mono">{v.license_plate}</p>
                        </div>
                        <div className="text-xs text-[#525866]">
                          Anteil: <strong>{v.supplier_share_pct != null ? v.supplier_share_pct : selectedSupplier.default_share_pct}%</strong>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex justify-between items-center">
                  <div className="text-xs">
                    <button className="text-[#525866] mr-3 underline" onClick={() => { const sel = {}; supplierVehicles.forEach((v) => sel[v.id] = true); setSelectedVehicleIds(sel); }}>Alle auswählen</button>
                    <button className="text-[#525866] underline" onClick={() => setSelectedVehicleIds({})}>Keine</button>
                  </div>
                  <Button className="btn-primary" onClick={runPreview} disabled={previewLoading || supplierVehicles.length === 0} data-testid="run-preview-btn">
                    <Calculator size={14} className="mr-1" />
                    {previewLoading ? 'Berechne…' : 'Vorschau berechnen'}
                  </Button>
                </div>
              </div>

              {/* Preview */}
              {preview && (
                <div className="card-swiss p-4" data-testid="preview-section">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-[#111418] flex items-center gap-2"><Money size={16} className="text-[#009951]" /> Vorschau</h3>
                    <span className="text-xs text-[#8A94A6]">{preview.period_from} – {preview.period_to} · Basis {preview.basis.toUpperCase()}</span>
                  </div>

                  <div className="overflow-x-auto border border-[#E2E4E9] rounded-sm">
                    <table className="w-full text-sm">
                      <thead className="bg-[#111418] text-white">
                        <tr>
                          <th className="text-left p-2 font-semibold">Fahrzeug</th>
                          <th className="text-right p-2 font-semibold">Buchungen</th>
                          <th className="text-right p-2 font-semibold">Umsatz (brutto)</th>
                          <th className="text-right p-2 font-semibold">Anteil %</th>
                          <th className="text-right p-2 font-semibold">Auszahlung (brutto)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedLineItems.map((li) => (
                          <tr key={li.vehicle_id} className="border-t border-[#E2E4E9] even:bg-[#F7F8FA]" data-testid={`preview-row-${li.vehicle_id}`}>
                            <td className="p-2">
                              <p className="font-semibold text-[#111418]">{li.brand} {li.model}</p>
                              <p className="text-xs text-[#8A94A6] font-mono">{li.license_plate}</p>
                            </td>
                            <td className="p-2 text-right tabular-nums">{li.bookings_count}</td>
                            <td className="p-2 text-right tabular-nums text-[#009951]">{eur(li.revenue)}</td>
                            <td className="p-2 text-right">
                              <Input
                                type="number" min={0} max={100} step={0.5}
                                value={shareOverrides[li.vehicle_id] !== undefined ? shareOverrides[li.vehicle_id] : li.share_pct}
                                onChange={(e) => overridePct(li.vehicle_id, e.target.value)}
                                className="w-20 text-right tabular-nums h-8"
                                data-testid={`share-input-${li.vehicle_id}`}
                              />
                            </td>
                            <td className="p-2 text-right tabular-nums font-bold text-[#E60000]">{eur(li.effective_payout)}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-[#111418] bg-[#F1F2F4] font-bold">
                          <td className="p-2">GESAMT</td>
                          <td></td>
                          <td className="p-2 text-right tabular-nums">{eur(totalRevenue)}</td>
                          <td></td>
                          <td className="p-2 text-right tabular-nums">{eur(totalGross)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div>
                      <Label className="overline mb-1 block">MwSt-Satz</Label>
                      <div className="flex gap-2">
                        <Button variant={vatRate === 19 ? 'default' : 'outline'} size="sm" className={vatRate === 19 ? 'btn-primary' : ''} onClick={() => setVatRate(19)}>19%</Button>
                        <Button variant={vatRate === 7 ? 'default' : 'outline'} size="sm" className={vatRate === 7 ? 'btn-primary' : ''} onClick={() => setVatRate(7)}>7%</Button>
                        <Button variant={vatRate === 0 ? 'default' : 'outline'} size="sm" className={vatRate === 0 ? 'btn-primary' : ''} onClick={() => setVatRate(0)}>0%</Button>
                      </div>
                    </div>
                    <div className="col-span-2 bg-[#F7F8FA] rounded-sm p-3 text-sm">
                      <div className="flex justify-between border-b border-[#111418] mb-1 pb-1 font-bold text-[#111418]"><span>Brutto-Auszahlung (Anteil vom Brutto-Umsatz):</span><span className="font-mono tabular-nums text-[#E60000]">{eur(totalGross)}</span></div>
                      <div className="flex justify-between text-[#525866]"><span>davon Netto:</span><span className="font-mono tabular-nums">{eur(totalNet)}</span></div>
                      <div className="flex justify-between text-[#525866]"><span>davon MwSt {vatRate}% (herausgerechnet):</span><span className="font-mono tabular-nums">{eur(totalVat)}</span></div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label className="overline mb-1 block">Notiz für die Gutschrift (optional)</Label>
                    <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Bemerkung, die im PDF erscheinen soll…" data-testid="settlement-notes" />
                  </div>

                  <div className="mt-3 flex flex-wrap justify-between items-center gap-2">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={postToCashbook} onChange={(e) => setPostToCashbook(e.target.checked)} className="w-4 h-4 accent-[#E60000]" data-testid="post-to-cashbook" />
                      <span>Automatisch im Kassenbuch als Ausgabe verbuchen (Kategorie <code>lieferanten_abrechnung</code>)</span>
                    </label>
                    <Button className="btn-primary" onClick={createSettlement} disabled={creating || totalGross <= 0} data-testid="create-settlement-btn">
                      <FilePdf size={14} className="mr-1" />
                      {creating ? 'Erstelle…' : 'Abrechnung erstellen & PDF laden'}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Supplier CRUD Dialog */}
      <Dialog open={showSupplierModal} onOpenChange={setShowSupplierModal}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] flex flex-col p-0"
          data-testid="supplier-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-[#E2E4E9] shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Buildings size={18} className="text-[#E60000]" />
              {supplierEditingId ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}
            </DialogTitle>
          </DialogHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="overline">Firma / Name *</Label>
                <Input value={supplierForm.name} onChange={(e) => setSupplierForm({ ...supplierForm, name: e.target.value })} data-testid="supplier-name-input" />
              </div>
              <div><Label className="overline">Ansprechpartner</Label><Input value={supplierForm.contact_person} onChange={(e) => setSupplierForm({ ...supplierForm, contact_person: e.target.value })} /></div>
              <div><Label className="overline">E-Mail</Label><Input type="email" value={supplierForm.email} onChange={(e) => setSupplierForm({ ...supplierForm, email: e.target.value })} /></div>
              <div><Label className="overline">Telefon</Label><Input value={supplierForm.phone} onChange={(e) => setSupplierForm({ ...supplierForm, phone: e.target.value })} /></div>
              <div><Label className="overline">Standard-Anteil %</Label><Input type="number" min={0} max={100} step={0.5} value={supplierForm.default_share_pct} onChange={(e) => setSupplierForm({ ...supplierForm, default_share_pct: parseFloat(e.target.value) || 0 })} data-testid="supplier-share-input" /></div>
              <div className="col-span-2"><Label className="overline">Straße</Label><Input value={supplierForm.address_street} onChange={(e) => setSupplierForm({ ...supplierForm, address_street: e.target.value })} /></div>
              <div><Label className="overline">PLZ</Label><Input value={supplierForm.address_zip} onChange={(e) => setSupplierForm({ ...supplierForm, address_zip: e.target.value })} /></div>
              <div><Label className="overline">Ort</Label><Input value={supplierForm.address_city} onChange={(e) => setSupplierForm({ ...supplierForm, address_city: e.target.value })} /></div>
              <div><Label className="overline">USt-IdNr.</Label><Input value={supplierForm.vat_id} onChange={(e) => setSupplierForm({ ...supplierForm, vat_id: e.target.value })} /></div>
              <div><Label className="overline">Steuernummer</Label><Input value={supplierForm.tax_number} onChange={(e) => setSupplierForm({ ...supplierForm, tax_number: e.target.value })} /></div>
              <div className="col-span-2"><Label className="overline">Bank</Label><Input value={supplierForm.bank_name} onChange={(e) => setSupplierForm({ ...supplierForm, bank_name: e.target.value })} /></div>
              <div><Label className="overline">IBAN</Label><Input value={supplierForm.iban} onChange={(e) => setSupplierForm({ ...supplierForm, iban: e.target.value })} /></div>
              <div><Label className="overline">BIC</Label><Input value={supplierForm.bic} onChange={(e) => setSupplierForm({ ...supplierForm, bic: e.target.value })} /></div>
              <div><Label className="overline">Zahlungsziel (Tage)</Label><Input type="number" min={0} value={supplierForm.payment_terms_days} onChange={(e) => setSupplierForm({ ...supplierForm, payment_terms_days: parseInt(e.target.value) || 0 })} /></div>
              <div className="col-span-2"><Label className="overline">Notiz</Label><Textarea rows={2} value={supplierForm.notes} onChange={(e) => setSupplierForm({ ...supplierForm, notes: e.target.value })} /></div>
            </div>
          </div>

          {/* Sticky footer */}
          <DialogFooter className="px-6 py-4 border-t border-[#E2E4E9] bg-white shrink-0">
            <Button variant="outline" onClick={() => setShowSupplierModal(false)}><X size={14} className="mr-1" /> Abbrechen</Button>
            <Button className="btn-primary" onClick={saveSupplier} data-testid="save-supplier-btn"><CheckCircle size={14} className="mr-1" /> Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierSettlements;
